import type { NewsletterMutationResponse, PostNewsletterMutationBody } from '@/types/newsletter';
import { useToastStore } from '@/store/useToastStore';
import { useMutation } from '@tanstack/react-query';

async function postNewsletterViaNextApi(
  path: 'subscribe' | 'unsubscribe',
  body: PostNewsletterMutationBody
): Promise<NewsletterMutationResponse> {
  const response = await fetch(`/api/newsletter/${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });

  let data: NewsletterMutationResponse;
  try {
    data = (await response.json()) as NewsletterMutationResponse;
  } catch {
    if (!response.ok) {
      throw new Error(`Error HTTP: ${response.status}`);
    }
    throw new Error('Respuesta inválida del servidor');
  }

  if (!response.ok) {
    throw new Error(
      data.error || data.message || `Error HTTP: ${response.status}`
    );
  }

  if (data.success === false) {
    throw new Error(data.error || data.message || 'Error al procesar la solicitud');
  }

  return {
    success: true,
    message: data.message,
  };
}

const subscribeToNewsletter = (body: PostNewsletterMutationBody) =>
  postNewsletterViaNextApi('subscribe', body);

const unsubscribeFromNewsletter = (body: PostNewsletterMutationBody) =>
  postNewsletterViaNextApi('unsubscribe', body);

export const useNewsletter = () => {
  const showToast = useToastStore((s) => s.showToast);

  const subscribeMutation = useMutation({
    mutationFn: subscribeToNewsletter,
    onSuccess: () => {
      showToast('¡Gracias por suscribirte al newsletter!', 'success');
    },
    onError: (error: Error) => {
      console.error('Error suscribiendo al newsletter:', error);
      showToast('No se pudo completar la suscripción', 'error');
    },
  });

  const unsubscribeMutation = useMutation({
    mutationFn: unsubscribeFromNewsletter,
    onSuccess: () => {
      showToast('Te has dado de baja del newsletter correctamente', 'success');
    },
    onError: (error: Error) => {
      console.error('Error dando de baja del newsletter:', error);
      showToast('No se pudo completar la baja del newsletter', 'error');
    },
  });

  return {
    subscribe: subscribeMutation.mutate,
    subscribeAsync: subscribeMutation.mutateAsync,
    isSubscribing: subscribeMutation.isPending,
    subscribeError: subscribeMutation.error?.message ?? null,
    isSubscribeError: subscribeMutation.isError,
    isSubscribeSuccess: subscribeMutation.isSuccess,
    subscribeData: subscribeMutation.data,
    resetSubscribe: subscribeMutation.reset,

    unsubscribe: unsubscribeMutation.mutate,
    unsubscribeAsync: unsubscribeMutation.mutateAsync,
    isUnsubscribing: unsubscribeMutation.isPending,
    unsubscribeError: unsubscribeMutation.error?.message ?? null,
    isUnsubscribeError: unsubscribeMutation.isError,
    isUnsubscribeSuccess: unsubscribeMutation.isSuccess,
    unsubscribeData: unsubscribeMutation.data,
    resetUnsubscribe: unsubscribeMutation.reset,
  };
};
